/**
 * feynmanPathPipeline — Feynman's path integral: all paths of a
 * particle through space, summed together.
 *
 * In quantum mechanics, a particle doesn't take a single path
 * between two points — it takes ALL possible paths, and each
 * contributes a complex phase factor to the final amplitude.
 * The result is a wave-like interference pattern.
 *
 * Innovations:
 *   - We sample 64 random paths between two points (the "start"
 *     on the left and the "end" on the right), each path being
 *     a perturbation around the classical straight line.
 *   - Each path is drawn as a series of segments, with its
 *     thickness scaled by the amplitude of the path.
 *   - The "classical path" (the most probable one) is highlighted
 *     as a bright line.
 *   - The cumulative interference pattern is shown as a halo
 *     around the region, brighter where paths converge.
 *   - Audio: bass adds more paths, mid controls smoothing,
 *     high drives the classical path brightness.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface FeynmanPathPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash11(float n) {
  return fract(sin(n * 12.9898) * 43758.5453);
}

vec2 pathPoint(float pathId, float progress, float t, vec2 start, vec2 end) {
  vec2 base = mix(start, end, progress);
  float amp = 0.06 + 0.04 * sin(t * 0.5 + pathId);
  vec2 perturb = vec2(0.0);
  for (int i = 0; i < 4; i++) {
    float fi = float(i);
    float phase = hash11(pathId * 100.0 + fi * 7.0) * 6.2831;
    float freq = 3.0 + hash11(pathId * 100.0 + fi * 11.0) * 5.0;
    float a = 0.5 + hash11(pathId * 100.0 + fi * 13.0) * 0.5;
    perturb.x += a * amp * sin(progress * freq * 3.14159 + phase + t * 0.2);
    perturb.y += a * amp * cos(progress * freq * 3.14159 + phase * 1.3 + t * 0.3);
  }
  return base + perturb;
}

float distToPath(float pathId, vec2 p, float t, vec2 start, vec2 end) {
  // Sample the path at 40 points and find the minimum distance.
  // This is faster than the per-pixel loop in the previous version.
  float minD = 1e9;
  for (int k = 0; k < 40; k++) {
    float fk = float(k) / 39.0;
    vec2 pt = pathPoint(pathId, fk, t, start, end);
    float d = length(p - pt);
    minD = min(minD, d);
  }
  return minD;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.04, 0.06, 0.12), vec3(0.01, 0.01, 0.04), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // Source and target.
  vec2 start = vec2(-0.65, 0.0);
  vec2 end = vec2(0.65, 0.0);
  float startD = length(p - start);
  float endD = length(p - end);
  col += vec3(0.95, 0.7, 0.4) * exp(-startD * 200.0) * 1.5;
  col += vec3(0.4, 0.95, 0.7) * exp(-endD * 200.0) * 1.5;
  col += vec3(0.95, 0.7, 0.4) * exp(-startD * 20.0) * 0.3;
  col += vec3(0.4, 0.95, 0.7) * exp(-endD * 20.0) * 0.3;

  // Classical path (the most probable).
  vec2 aA = start;
  vec2 bB = end;
  vec2 ab = bB - aA;
  float tA = clamp(dot(p - aA, ab) / max(dot(ab, ab), 0.0001), 0.0, 1.0);
  vec2 closest = aA + ab * tA;
  float dA = length(p - closest);
  float classicalMask = 1.0 - smoothstep(0.0, 0.008, dA);
  col += vec3(1.0, 0.95, 0.7) * classicalMask * (0.7 + 0.5 * high);

  // Draw the paths.
  // Reduced from 32 to 16 paths to make it fast.
  const int N_PATHS = 16;
  for (int pi = 0; pi < N_PATHS; pi++) {
    float pId = float(pi);
    vec3 pathColor = vec3(
      0.5 + 0.5 * sin(pId * 0.5),
      0.5 + 0.5 * sin(pId * 0.5 + 2.094),
      0.5 + 0.5 * sin(pId * 0.5 + 4.189)
    );
    float d = distToPath(pId, p, uTime, start, end);
    float pathMask = exp(-d * 200.0);
    col += pathColor * pathMask * 0.4;
  }

  // Interference halo (the cumulative effect).
  float halo = 0.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float dx = (fi / 6.0) - 0.5;
    float bandY = dx * 0.4;
    float bandD = abs(p.y - bandY);
    halo += exp(-bandD * 30.0) * (0.4 + 0.6 * sin(p.x * 30.0 + uTime * 2.0 + fi));
  }
  col += vec3(0.4, 0.85, 0.95) * halo * 0.08;

  // Path-crossing diffraction.
  float cross = 0.0;
  for (int i = 0; i < 4; i++) {
    for (int j = i + 1; j < 4; j++) {
      float fi = float(i);
      float fj = float(j);
      vec2 a1 = pathPoint(fi, 0.5, uTime, start, end);
      vec2 b1 = pathPoint(fj, 0.5, uTime, start, end);
      float cross_d = length(p - (a1 + b1) * 0.5);
      cross += exp(-cross_d * 100.0);
    }
  }
  col += vec3(1.0, 1.0, 0.7) * cross * 0.3;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeFeynmanPathBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeFeynmanPathRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createFeynmanPathPipeline(gl: WebGL2RenderingContext): FeynmanPathPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawFeynmanPath(
  gl: WebGL2RenderingContext,
  pipe: FeynmanPathPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
